import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core'
import { BaseWidgetComponent } from '../base-widget/base-widget.component'
import { CommonModule } from '@angular/common'
import { IPGWidget } from 'htv-lib'
import { LocalChannel } from '../../interfaces/local-channel'
import { EpgService } from '../../services/epg.service'
import { LocalEPGProgram } from '../../interfaces/local-epg'
import moment from 'moment'
import { PAGE_KEY } from '../../constants/page.constants'
import { MomentPipe } from '../../pipes/moment.pipe'
import { SmartAppService } from '../../services/smart-app.service'
import { LocalApp, LocalAppLaunchMode } from '../../interfaces/local-app'
import { ChannelService } from '../../services/channel.service'
import { TranslateModule } from '@ngx-translate/core'
import { ProgramTitlePipe } from '../../pipes/program-title.pipe'

interface IPGChannel {
  channel: LocalChannel
  programs: IPGProgram[]
  rowWidth: number
}

interface IPGProgram {
  width: number
  offset: number
  categoryColor?: string
  start: Date
  end: Date
  program?: LocalEPGProgram
}

interface IPGTime {
  offset: number
  value: string
}

@Component({
  selector: 'app-ipg-grid',
  standalone: true,
  imports: [CommonModule, MomentPipe, TranslateModule, ProgramTitlePipe],
  templateUrl: './ipg-grid.component.html',
  styleUrls: ['./ipg-grid.component.scss'],
})
export class IPGGridComponent extends BaseWidgetComponent implements OnInit {
  locale = moment.locale()

  channels!: LocalChannel[]
  channelsIPG!: { [key: string]: IPGChannel }
  localApps: LocalApp[] = []
  shouldShowChannelIcons = true
  shouldShowSmartApps = false

  startDate = moment()
  cutoffDate!: moment.Moment
  minDate!: moment.Moment
  maxDate!: moment.Moment
  currentScroll = 0
  currentScrollTime = moment()
  minOffset = 0

  times: IPGTime[] = []
  timelineOffset = 40

  readonly stepSize = 8 // 1 step = 1 minute
  readonly scrollStep = this.stepSize * 15 // 15 minutes
  readonly centerOffset = 256 // Avoid positioning full left
  readonly marginOffset = 6 // $ipg-row-padding * 2 from scss
  readonly finalProgramWidth = 1000

  selectedChannel?: LocalChannel
  selectedProgram?: IPGProgram
  selectedProgramIndex?: number
  selectedTileKey: string | null = null
  selectedTileClickCount = 0

  @ViewChild('timeline') timeline!: ElementRef<HTMLDivElement>
  @ViewChildren('programs') programs!: QueryList<ElementRef>

  constructor(
    private epgService: EpgService,
    private smartAppService: SmartAppService,
    private channelService: ChannelService
  ) {
    super()
  }

  override ngOnInit(): void {
    super.ngOnInit()

    this.initConfigProperties()
    this.initChannels()
    this.initSmartApps()
  }

  initConfigProperties(): void {
    const config = this.config as IPGWidget
    this.shouldShowChannelIcons = config.showChannelIcons ?? true
    this.shouldShowSmartApps = config.showSmartApps ?? false
  }

  override applyStyles(): void {
    super.applyStyles()

    const config = this.config as IPGWidget
    const programInnerStyle: Record<string, string | number> = {}
    const programSelectedStyle: Record<string, string | number> = {}
    const appProgramSelectedStyle: Record<string, string | number> = {}
    const noProgramMessageSelectedStyle: Record<string, string | number> = {}
    const headerInnerStyle: Record<string, string | number> = {}
    const headerTimelineTimeStyle: Record<string, string | number> = {}
    const channelInnerStyle: Record<string, string | number> = {}

    if (config.itemColor) {
      programInnerStyle['color'] = config.itemColor
    }

    if (config.itemBackgroundColor) {
      programInnerStyle['background-color'] = config.itemBackgroundColor
    }

    if (config.focusColor) {
      programSelectedStyle['color'] = config.focusColor
      appProgramSelectedStyle['color'] = config.focusColor
      noProgramMessageSelectedStyle['color'] = config.focusColor
    }

    if (config.focusBackgroundColor) {
      programSelectedStyle['background-color'] = config.focusBackgroundColor
      appProgramSelectedStyle['background-color'] = config.focusBackgroundColor
      noProgramMessageSelectedStyle['background-color'] = config.focusBackgroundColor
    }

    if (config.headerColor) {
      headerInnerStyle['color'] = config.headerColor
      headerTimelineTimeStyle['color'] = config.headerColor
      channelInnerStyle['color'] = config.headerColor
    }

    if (config.headerBackgroundColor) {
      headerInnerStyle['background-color'] = config.headerBackgroundColor
      channelInnerStyle['background-color'] = config.headerBackgroundColor
    }

    if (config.elementFontSize) {
      programInnerStyle['font-size'] = `${this.scaleToResolution(config.elementFontSize)}px`
    }

    if (config.headerFontSize) {
      headerInnerStyle['font-size'] = `${this.scaleToResolution(config.headerFontSize)}px`
      headerTimelineTimeStyle['font-size'] = `${this.scaleToResolution(config.headerFontSize)}px`
      channelInnerStyle['font-size'] = `${this.scaleToResolution(config.headerFontSize)}px`
    }

    this.setRule('.program-inner', programInnerStyle)
    this.setRule('.program.htv-selected .program-inner', programSelectedStyle)
    this.setRule('.app-program.htv-selected .program-inner', appProgramSelectedStyle)
    this.setRule('.no-programs.htv-selected .no-program-message', noProgramMessageSelectedStyle)
    this.setRule('.header__inner', headerInnerStyle)
    this.setRule('.header__timeline__times__time', headerTimelineTimeStyle)
    this.setRule('.channel__inner', channelInnerStyle)
  }

  initChannels(): void {
    const channelsEPG = this.epgService.channelsEPG
    this.channels = this.epgService.channels

    const cutoffTime = moment().subtract(30, 'minutes')
    this.cutoffDate = cutoffTime.clone()

    const minOffset = 0
    let maxRowWidth = 0
    let minStart = cutoffTime.clone()
    let maxEnd = moment()

    const channelsIPG: { [key: string]: IPGChannel } = {}

    for (let i = 0; i < this.channels.length; i++) {
      const channel = this.channels[i]
      if (channel && channelsEPG[channel.id]) {
        let rowWidth = 0
        const programs = channelsEPG[channel.id].programs
          .filter(program => program.duration && moment(program.end).isAfter(cutoffTime))
          .map(program => {
            const momentStart = moment(program.start)
            const momentEnd = moment(program.end)

            const effectiveStart = momentStart.isBefore(cutoffTime) ? cutoffTime : momentStart

            const adjustedDuration = momentEnd.diff(effectiveStart, 'minutes')
            const width = adjustedDuration * this.stepSize - this.marginOffset
            rowWidth += width

            const offset = this.getOffset(effectiveStart)
            if (effectiveStart.isBefore(minStart)) {
              minStart = effectiveStart
            }
            if (momentEnd.isAfter(maxEnd)) {
              maxEnd = momentEnd
            }

            const ipgProgram: IPGProgram = {
              width: width,
              offset: offset,
              start: program.start,
              end: program.end,
              program: program,
            }

            // TEST: Assigning random category
            // Uncomment to test
            /*
            const random = _categories.length * Math.random() << 0
            const category = _categories[random]
            program.category = category.name
            if (program.category) {
              const index = this.categories.findIndex(c => c.name === program.category)
              if (index < 0) {
                this.categories.push(_categories.find(c => c.name === program.category)!)
              }
            }
            ipgProgram.categoryColor = category.color
            */

            return ipgProgram
          })
        channelsIPG[channel.id] = {
          channel: channel,
          programs: programs,
          rowWidth: rowWidth,
        }

        if (rowWidth > maxRowWidth) {
          maxRowWidth = rowWidth
        }
      } else {
        channelsIPG[channel.id] = {
          channel: channel,
          programs: [],
          rowWidth: 0,
        }
      }
    }

    // Add final program that will be hidden for better display
    for (const channelId in channelsIPG) {
      const ipgChannel = channelsIPG[channelId]
      if (ipgChannel && ipgChannel.programs && ipgChannel.programs.length > 0) {
        const programs = ipgChannel.programs
        const lastProgram = programs[programs.length - 1]
        const hiddenProgramWidth = maxRowWidth - ipgChannel.rowWidth + this.finalProgramWidth
        ipgChannel.programs.push({
          width: hiddenProgramWidth,
          offset: lastProgram.offset + lastProgram.width,
          categoryColor: lastProgram.categoryColor,
          start: new Date(),
          end: new Date(),
        })
      }
    }

    this.channelsIPG = channelsIPG
    this.minOffset = minOffset
    this.minDate = minStart
    this.maxDate = maxEnd

    // Generate timeline times
    const timesMinStart = moment(this.minDate)
    const timesMaxEnd = moment(this.maxDate)
    for (
      let i = timesMinStart, steps = timesMinStart.diff(timesMaxEnd, 'minutes') / 30;
      i <= timesMaxEnd;
      i = i.add(30, 'minutes'), steps++
    ) {
      const momentDate = moment(i)
      const offset = momentDate.diff(this.cutoffDate, 'minutes', true) * this.stepSize
      this.times.push({
        offset: offset,
        value: momentDate.format('LT'),
      })
    }

    // Find first program to focus
    for (const channelId in channelsIPG) {
      const ipgChannel = channelsIPG[channelId]
      if (ipgChannel.programs.length > 0) {
        const now = moment()
        const firstChannel = ipgChannel.channel
        const firstProgramIndex = ipgChannel.programs.findIndex(ipgProgram => {
          return now.isAfter(ipgProgram.start) && now.isBefore(ipgProgram.end)
        })
        this.selectedChannel = firstChannel
        if (firstProgramIndex > -1) {
          this.selectedProgram = ipgChannel.programs[firstProgramIndex]
          this.selectedProgramIndex = firstProgramIndex
        }

        this.channelService.selectChannel(firstChannel)
        this.selectedTileKey = `${firstChannel.id}-${firstProgramIndex > -1 ? firstProgramIndex : 'no-program'}`
        this.selectedTileClickCount = 1

        this.pageService.updateSubject(PAGE_KEY.SELECTED_APP, undefined)
        this.pageService.updateSubject(PAGE_KEY.SELECTED_CHANNEL, this.selectedChannel)
        this.pageService.updateSubject(PAGE_KEY.SELECTED_PROGRAM, this.selectedProgram?.program)

        break
      }
    }

    // Cursor initialization
    /*
    this.subscriptions.add(
      interval(1000).subscribe(() => {
        const m = moment()
        this.cursorOffset = this.getOffset(m)
        this.cursorTime = m.format('LT')
      })
    )
    */
  }

  getOffset(date: moment.Moment): number {
    return date.diff(this.cutoffDate, 'minutes', true) * this.stepSize
  }

  onClick(event: any, channel: LocalChannel, program: IPGProgram, programIndex: number): void {
    const tileKey = `${channel.id}-${programIndex}`

    if (this.selectedTileKey !== tileKey) {
      this.selectedTileKey = tileKey
      this.selectedTileClickCount = 1
    } else {
      this.selectedTileClickCount++
    }

    this.selectedChannel = channel
    this.selectedProgram = program
    this.selectedProgramIndex = programIndex
    this.scroll(event.target.offsetLeft - this.centerOffset)
    this.pageService.updateSubject(PAGE_KEY.SELECTED_APP, undefined)
    this.pageService.updateSubject(PAGE_KEY.SELECTED_CHANNEL, this.selectedChannel)
    this.pageService.updateSubject(PAGE_KEY.SELECTED_PROGRAM, this.selectedProgram.program)

    if (this.selectedTileClickCount === 2) {
      this.channelService.selectChannel(channel)
    } else if (this.selectedTileClickCount >= 3) {
      this.channelService.openChannelPage(channel)
      this.selectedTileKey = null
      this.selectedTileClickCount = 0
    }
  }

  onNoProgramClick(channel: LocalChannel): void {
    const tileKey = `${channel.id}-no-program`

    if (this.selectedTileKey !== tileKey) {
      this.selectedTileKey = tileKey
      this.selectedTileClickCount = 1
    } else {
      this.selectedTileClickCount++
    }

    this.selectedChannel = channel
    this.selectedProgram = undefined
    this.selectedProgramIndex = undefined
    this.pageService.updateSubject(PAGE_KEY.SELECTED_CHANNEL, this.selectedChannel)
    this.pageService.updateSubject(PAGE_KEY.SELECTED_PROGRAM, undefined)

    if (this.selectedTileClickCount === 2) {
      this.channelService.selectChannel(channel)
    } else if (this.selectedTileClickCount >= 3) {
      this.channelService.openChannelPage(channel)
      this.selectedTileKey = null
      this.selectedTileClickCount = 0
    }
  }

  onAppClick(app: LocalApp): void {
    const tileKey = `${app.id}-application`

    if (this.selectedTileKey !== tileKey) {
      this.selectedTileKey = tileKey
      this.selectedTileClickCount = 1
    } else {
      this.selectedTileClickCount++
    }

    this.pageService.updateSubject(PAGE_KEY.SELECTED_APP, app)
    this.pageService.updateSubject(PAGE_KEY.SELECTED_CHANNEL, undefined)
    this.pageService.updateSubject(PAGE_KEY.SELECTED_PROGRAM, undefined)

    if (this.selectedTileClickCount === 1) {
      // First click: Only update selection (already done above)
    } else if (this.selectedTileClickCount === 2) {
      this.channelService.selectChannel(undefined)
    } else if (this.selectedTileClickCount >= 3) {
      this.smartAppService.launchApp(app.id, LocalAppLaunchMode.APPS)
      this.selectedTileKey = null
      this.selectedTileClickCount = 0
    }
  }

  scrollLeft(): void {
    this.scroll(this.currentScroll - this.scrollStep)
  }

  scrollRight(): void {
    this.scroll(this.currentScroll + this.scrollStep)
  }

  scroll(value: number): void {
    // Apply offset to timeline
    this.timeline.nativeElement.scrollLeft = value

    // Apply offset to all rows
    this.programs.forEach(program => {
      program.nativeElement.scrollLeft = value
    })

    this.currentScroll = value
    const currentScrollMinutes = (this.currentScroll + this.centerOffset) / this.stepSize
    this.currentScrollTime = moment(this.cutoffDate).add(currentScrollMinutes, 'minutes')
  }

  initSmartApps(): void {
    if (this.shouldShowSmartApps) {
      this.localApps = this.smartAppService.localApps.filter(localApp => localApp.available)
    }
  }
}
